import React, { useState } from 'react';
import ValidationGroup from 'devextreme-react/validation-group';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { MarsApiConfig as apiConfig } from '../../../api/mars-api-config';
import { MarsApiService as api } from '../../../api/mars-api-service';
import { useLoaderContext } from '../../../contexts/LoaderContext';
import TextBox from 'devextreme-react/text-box';
import icons from '../../common/icons/icons';
import Tooltip from 'devextreme-react/tooltip';
import Validator, { StringLengthRule } from 'devextreme-react/validator';
import countryList from '../../../components/Country.json';
import { ILiveVesselData } from '../../../types/live-vessel-data';

export default function FleetVesselFormFields(props: {
  onVesselIMOChange: (imo: string) => void;
  onVesselDataChange?: (data: ILiveVesselData) => void;
}) {
  const { t } = useTranslation();
  const { setValue } = useFormContext();

  const { setIsLoading } = useLoaderContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [vesselData, setVesselData] = useState<ILiveVesselData>({
    imoNumber: '',
    vesselName: '',
    flag: '',
  });

  const [filteredCountryList, setFilteredCountryList] = useState<any>(null);

  const handleSearchClick = async () => {
    if (!searchTerm) return;

    try {
      const res = await api.get(
        { url: `${apiConfig.vessels}/${searchTerm}` },
        setIsLoading
      );

      const imoNumber = res.imoNumber || '';
      const vesselName = res.vesselName || '';
      const flag = res.iso3 || '';

      const country = countryList.find(
        (countryItem) => countryItem.iso_3 === res.iso3
      );
      setFilteredCountryList(country);

      setValue('vesselIMO', imoNumber);
      setValue('vesselName', vesselName);
      setValue('flag', flag);

      // Update local state
      setVesselData({ imoNumber, vesselName, flag });
      props.onVesselIMOChange(imoNumber);
      if (props.onVesselDataChange) {
        props.onVesselDataChange({ imoNumber, vesselName, flag });
      }
    } catch (error) {
      console.error('Error fetching vessel data:', error);
    }
  };

  return (
    <>
      <div
        className="m-c-header-section"
        style={{
          backgroundColor: '#ffffff',
          borderBottom: 'none',
        }}
      >
        <div className="m-l-header-left">
          <div
            className="m-l-header-search-area-block-add-vessel"
            data-testid="searchInput"
          >
            <ValidationGroup>
              <TextBox
                id="searchbox"
                placeholder={t('labels.vesselSearchByIMO')}
                className="m-c-input-control"
                value={searchTerm}
                onValueChanged={(e) => setSearchTerm(e.value)}
                onEnterKey={handleSearchClick}
              >
                <Validator>
                  <StringLengthRule
                    min={3}
                    ignoreEmptyValue={true}
                    message={t('errors.searchWithLessThan2chara')}
                  />
                </Validator>
              </TextBox>
            </ValidationGroup>
            <Tooltip
              target={'#searchbox'}
              showEvent="dxhoverstart"
              hideEvent="dxhoverend"
              position="right"
              contentRender={() => {
                return (
                  <div className="m-l-tooltip-wrapper">
                    {t('labels.searchInfo')}
                  </div>
                );
              }}
            />
            <div
              className="m-c-search-icon"
              onClick={handleSearchClick}
              data-testid="vessal-search"
            >
              <icons.Search />
            </div>
          </div>
        </div>
      </div>
      {vesselData.imoNumber != '' ? (
        <div
          className="row m-l-advance-search-body-block u-pb0"
          style={{ paddingTop: '80px', paddingLeft: '14px' }}
        >
          <div className="col-md-3 col-sm-6 col-lg-3 col-xs-12">
            <div className="m-l-advance-search-item-block mb-3">
              <span
                style={{
                  color: '#4f4f4f',
                  fontSize: '12px',
                  fontWeight: 600,
                  fontFamily: 'Poppins, sans-serif',
                }}
              >
                {t('labels.vesselImo')}
              </span>
              <h5
                style={{
                  color: '#585c64',
                  fontSize: '14px',
                  fontWeight: 400,
                  marginBottom: 0,
                  marginTop: '10px',
                  fontFamily: 'Poppins, sans-serif',
                }}
              >
                {vesselData?.imoNumber}
              </h5>
            </div>
          </div>
          <div className="col-md-3 col-sm-6 col-lg-3 col-xs-12">
            <div className="m-l-advance-search-item-block mb-3">
              <span
                style={{
                  color: '#4f4f4f',
                  fontSize: '12px',
                  fontWeight: 600,
                  fontFamily: 'Poppins, sans-serif',
                }}
              >
                {t('labels.vesselName')}
              </span>
              <h5
                style={{
                  color: '#585c64',
                  fontSize: '14px',
                  fontWeight: 400,
                  marginBottom: 0,
                  marginTop: '10px',
                  fontFamily: 'Poppins, sans-serif',
                }}
              >
                {vesselData.vesselName}
              </h5>
            </div>
          </div>
          <div className="col-md-3 col-sm-6 col-lg-3 col-xs-12">
            <div className="m-l-advance-search-item-block mb-3">
              <span
                style={{
                  color: '#4f4f4f',
                  fontSize: '12px',
                  fontWeight: 600,
                  fontFamily: 'Poppins, sans-serif',
                }}
              >
                {t('labels.flag')}
              </span>
              <h5
                style={{
                  color: '#585c64',
                  fontSize: '14px',
                  fontWeight: 400,
                  marginBottom: 0,
                  marginTop: '10px',
                  fontFamily: 'Poppins, sans-serif',
                }}
              >
                <img
                  src={filteredCountryList?.flag}
                  className="img-responsive"
                  alt="flag"
                  height={20}
                  width={20}
                  style={{ marginRight: '8px' }}
                />
                {vesselData.flag}
              </h5>
            </div>
          </div>
        </div>
      ) : (
        <div> </div>
      )}
    </>
  );
}
